import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Shield, 
  Volume2, 
  QrCode, 
  Camera, 
  Brain, 
  CheckCircle2, 
  Sparkles,
  ArrowRight,
  Zap,
  Clock,
  Globe,
  Heart,
  AlertTriangle,
  Accessibility,
  Pill,
  FileText,
  Smartphone
} from 'lucide-react';
import '../styles/LandingPage.css';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered OCR',
      description: 'Advanced vision AI reads handwritten & printed prescriptions',
      color: 'var(--primary-500)'
    },
    {
      icon: Shield,
      title: 'Safety Analysis',
      description: 'Instant drug interaction detection & safety scoring',
      color: 'var(--danger)'
    },
    {
      icon: Volume2,
      title: 'Voice Guide',
      description: 'Audio instructions for medicine schedules & dosages',
      color: 'var(--secondary-500)'
    },
    {
      icon: QrCode,
      title: 'QR Stickers',
      description: 'Printable medicine stickers with voice playback',
      color: 'var(--accent-purple)'
    }
  ];

  const steps = [
    { icon: Camera, title: 'Capture', description: 'Take a photo of your prescription' },
    { icon: Brain, title: 'Analyze', description: 'AI extracts medicine information' },
    { icon: Shield, title: 'Check', description: 'Get safety alerts & interactions' },
    { icon: Volume2, title: 'Listen', description: 'Voice instructions for each medicine' },
    { icon: QrCode, title: 'Generate', description: 'Create QR codes for medicine bottles' },
    { icon: Pill, title: 'Organize', description: 'Save to your medicine cabinet' }
  ];

  const benefits = [
    { icon: Zap, title: 'Instant Results', description: 'Analysis in seconds, not hours' },
    { icon: Clock, title: '100% Free', description: 'No subscriptions or hidden fees' },
    { icon: Globe, title: 'Multi-Language', description: 'English & Bengali supported' },
    { icon: Heart, title: 'Patient Safety', description: 'Drug interaction warnings' },
    { icon: AlertTriangle, title: 'Smart Alerts', description: 'Critical safety notifications' },
    { icon: Accessibility, title: 'Accessible', description: 'Voice support for all users' }
  ];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        {/* Animated Background Elements */}
        <div className="hero-bg-elements">
          <div className="bg-gradient-1" />
          <div className="bg-gradient-2" />
          <div className="bg-grid" />
          <motion.div 
            className="floating-pill pill-1"
            animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <Pill size={32} />
          </motion.div>
          <motion.div 
            className="floating-pill pill-2"
            animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <FileText size={28} />
          </motion.div>
          <motion.div 
            className="floating-pill pill-3"
            animate={{ y: [0, -15, 0], rotate: [0, 15, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Shield size={24} />
          </motion.div>
        </div>

        <div className="hero-container">
          <motion.div 
            className="hero-content"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div className="hero-badge" variants={itemVariants}>
              <Sparkles size={14} />
              <span>AI-Powered Prescription Analysis</span>
            </motion.div>

            <motion.h1 variants={itemVariants}>
              Your Personal
              <span className="gradient-text"> Medicine Safety </span>
              Assistant
            </motion.h1>

            <motion.p className="hero-description" variants={itemVariants}>
              Upload any prescription — handwritten or printed — and get instant safety analysis, 
              drug interaction warnings, voice instructions, and printable QR code stickers. 
              All powered by advanced AI, completely free.
            </motion.p>

            <motion.div className="hero-cta" variants={itemVariants}>
              <button
                className="btn btn-primary btn-xl"
                onClick={() => navigate('/upload')}
              >
                <Upload size={20} />
                Upload Prescription
                <ArrowRight size={18} />
              </button>
              <button
                className="btn btn-outline btn-xl"
                onClick={() => navigate('/cabinet')}
              >
                <Pill size={20} />
                Medicine Cabinet
              </button>
            </motion.div>

            <motion.div className="hero-stats" variants={itemVariants}>
              <div className="stat-item">
                <span className="stat-value">100%</span>
                <span className="stat-label">Free Forever</span>
              </div>
              <div className="stat-divider" />
              <div className="stat-item">
                <span className="stat-value">&lt;30s</span>
                <span className="stat-label">Analysis Time</span>
              </div>
              <div className="stat-divider" />
              <div className="stat-item">
                <span className="stat-value">AI</span>
                <span className="stat-label">Powered</span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div 
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="phone-mockup">
              <div className="phone-frame">
                <div className="phone-notch" />
                <div className="phone-screen">
                  <div className="screen-header">
                    <div className="screen-title">Analysis Results</div>
                  </div>
                  <div className="screen-content">
                    <motion.div 
                      className="result-card"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                    >
                      <div className="result-icon safety-high">
                        <CheckCircle2 size={20} />
                      </div>
                      <div className="result-info">
                        <div className="result-label">Safety Score</div>
                        <div className="result-value">92/100</div>
                      </div>
                    </motion.div>
                    <motion.div 
                      className="medicine-card"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1 }}
                    >
                      <Pill size={18} className="medicine-icon" />
                      <div className="medicine-info">
                        <div className="medicine-name">Amlodipine</div>
                        <div className="medicine-dose">10mg · 1+0+1</div>
                      </div>
                    </motion.div>
                    <motion.div 
                      className="medicine-card"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 }}
                    >
                      <Pill size={18} className="medicine-icon" />
                      <div className="medicine-info">
                        <div className="medicine-name">Omeprazole</div>
                        <div className="medicine-dose">20mg · Before meals</div>
                      </div>
                    </motion.div>
                    <motion.div 
                      className="action-buttons"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.4 }}
                    >
                      <button className="action-btn">
                        <Volume2 size={16} />
                      </button>
                      <button className="action-btn">
                        <QrCode size={16} />
                      </button>
                    </motion.div>
                  </div>
                </div>
              </div>
              <div className="phone-glow" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Features</span>
            <h2>Everything You Need for Medicine Safety</h2>
            <p>Advanced AI technology combined with user-friendly design</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  <div className="feature-icon-wrapper" style={{ background: `${feature.color}15` }}>
                    <Icon size={28} style={{ color: feature.color }} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="section-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Process</span>
            <h2>How It Works</h2>
            <p>Simple steps to safer medication management</p>
          </motion.div>

          <div className="steps-timeline">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  className="step-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="step-number">{index + 1}</div>
                  <div className="step-icon-wrapper">
                    <Icon size={28} />
                  </div>
                  <div className="step-content">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-badge">Benefits</span>
            <h2>Why Choose MedCheck?</h2>
            <p>Built with your health and convenience in mind</p>
          </motion.div>

          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  className="benefit-card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="benefit-icon">
                    <Icon size={22} />
                  </div>
                  <div className="benefit-content">
                    <h4>{benefit.title}</h4>
                    <p>{benefit.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <motion.div 
          className="cta-container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Smartphone size={48} className="cta-icon" />
            </motion.div>
            <h2>Start Protecting Your Health Today</h2>
            <p>Upload your first prescription and experience the future of medicine safety</p>
            <button
              className="btn btn-primary btn-xl"
              onClick={() => navigate('/upload')}
            >
              <Upload size={20} />
              Get Started Free
              <ArrowRight size={18} />
            </button>
          </div>
          <div className="cta-bg-pattern" />
        </motion.div>
      </section>
    </div>
  );
};
